/** @format */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LabelInput from "../../../shared/components/labelInput";
import useQuoteStore from "../store/QuoteStore";
import useAppStateStore from "../../../shared/store/AppStateStore";
import Tq6Mapper from "../mappers/tq6Mapper";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import QuoteStep from "../enums/QuoteStep";
import { handleNext } from "../../../shared/utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import {
  saveQuote,
  fetchPanelData,
  fetchPolicyDetails,
} from "../services/quoteService";
import {
  fetchOverNight,
  fetchKeeper,
  fetchOwner,
} from "../../../shared/services/simpleListItemsService";
import {
  downloadPrivacyPolicy,
  downloadTOBA,
} from "../../../shared/utils/apiUtils";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import RadioGroup from "../../../shared/components/RadioGroup";
import AddressLookup from "../../../shared/components/addressLookup";
import FieldStack from "../../../shared/components/FieldStack";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";

const Tq6 = () => {
  // Store
  const navigate = useNavigate();
  // Form Setup
  const {
    register,
    formState: { errors, isValid },
    watch,
    control,
    trigger,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: Tq6Mapper.get(),
  });

  const STATE = useAppStateStore();
  // Use flat state keys as per AppStateStore

  // State
  const [overNight, setOverNight] = useState([]);
  const [keeper, setKeeper] = useState([]);
  const [owner, setOwner] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addressError, setAddressError] = useState("");

  const convertArrayFormat = (array) => {
    if (!Array.isArray(array)) {
      return [];
    }
    return array.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
    }));
  };

  // Effects
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [overNightData, keeperData, ownerData] = await Promise.all([
          fetchOverNight(),
          fetchKeeper(),
          fetchOwner(),
        ]);

        setOverNight(overNightData);
        setKeeper(keeperData);
        setOwner(ownerData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ6_ENTERED);
  }, []);

  const overNightHome = watch("overNightHome");
  const isGarageAddressRequired = overNightHome === "No";
  const isGarageAddressSelected = Boolean(watch("lookUpAddress"));
  const isGarageManualAddressOpen =
    isGarageAddressRequired && STATE.enterAddressGarage;

  const resetGarageAddress = () => {
    setAddressError("");
    setValue("lookUpAddress", null);
    setValue("address", "");
    setValue("postcode", "");
    setValue("city", "");
    setValue("county", "");
    STATE.setLookupAddressGarage(null);
    STATE.setEnterAddressGarage(false);
    STATE.setShowAddressLookupbtnGarage(true);
  };

  // Event Handlers
  const onSubmit = async (data) => {
    if (isGarageManualAddressOpen) {
      return;
    }
    if (isGarageAddressRequired && !isGarageAddressSelected) {
      setAddressError("Please look up and select an address.");
      return;
    }
    setAddressError("");

    await new Promise((resolve) => {
      Tq6Mapper.set(data);
      setTimeout(resolve, 100);
    });
    if (isValid) {
      TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ6_NEXT_CLICKED);
    }
    const quoteData = useQuoteStore.getState().quote;
    //temporary uber driver id fix
    if (
      quoteData?.risk?.drivers?.[0]?.uberDriverId ===
      "UBER_TEST_USER_2_BRAMWELL"
    ) {
      navigate("/");
    }
    const latestClaims = useAppStateStore.getState().claims;
    const latestAdditionalDriver = useAppStateStore.getState().additionalDriver;
    if (latestClaims === "Yes") {
      handleNext(QuoteStep.TQ_CLAIM);
    } else if (latestAdditionalDriver === "Yes") {
      handleNext(QuoteStep.TQ_ADD_DRIVER);
    } else {
      saveQuote(quoteData, navigate).then((data) => {
        fetchPolicyDetails().catch((err) => {
          console.error("Policy details fetch failed:", err);
        });
        fetchPanelData(navigate, null, data.risk.riskObjectId).then(() => {
          handleNext(QuoteStep.TQ_PANEL);
        });
      });
      handleNext(QuoteStep.TQ_PANEL_LOADER);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QuoteFormWrapper>
          <QuoteStepHeading
            title="Vehicle Details"
            description="We're almost there. Just a few more details to complete your quote."
          />

          <FieldStack className="tw-text-sm">
            <>
              <RadioGroup
                name="RegOwner"
                mainLabel="Are you the registered owner of your vehicle?"
                register={register}
                trigger={trigger}
                errors={errors.RegOwner}
              />
              {watch("RegOwner") === "No" && (
                <LabelInput
                  id="owner"
                  label="Who is the registered owner of your vehicle?"
                  type="select"
                  control={control}
                  options={convertArrayFormat(owner)}
                  placeholder="Enter registered owner "
                  register={register("owner", {
                    required: "Required",
                  })}
                  errors={errors.owner}
                  watch={watch("owner")}
                  trigger={trigger}
                  isLoading={isLoading}
                />
              )}
              <RadioGroup
                name="Regkeeper"
                mainLabel="Are you the registered keeper of your vehicle?"
                register={register}
                trigger={trigger}
                errors={errors.Regkeeper}
              />

              {watch("Regkeeper") === "No" && (
                <LabelInput
                  id="keeper"
                  label="Who is the registered keeper of your vehicle?"
                  type="select"
                  control={control}
                  options={convertArrayFormat(keeper)}
                  placeholder="Enter registered keeper "
                  register={register("keeper", {
                    required: "Required",
                  })}
                  errors={errors.keeper}
                  watch={watch("keeper")}
                  trigger={trigger}
                  isLoading={isLoading}
                />
              )}
              <LabelInput
                id="marketValue"
                label="What is the estimated market value of your vehicle?"
                type="number"
                placeholder="e.g. 5,000"
                prefix="£"
                register={register("marketValue", {
                  required: "Required",
                  maxLength: {
                    value: 6,
                    message: "Price must be a number with at most 6 digits",
                  },
                  pattern: {
                    value: /^[0-9]{1,6}$/,
                    message: "Price must be a number with at most 6 digits",
                  },
                })}
                errors={errors.marketValue}
                // pattern="[0-9]{6}"
                watch={watch("marketValue")}
                trigger={trigger}
                maxLength={6}
                onInput={(e) => {
                  // Enforce numeric input and restrict to 6 digits
                  e.target.value = e.target.value.slice(0, 6);
                }}
              />

              <LabelInput
                id="locOverNight"
                label="Where is your vehicle located overnight?"
                type="select"
                options={convertArrayFormat(overNight)}
                control={control}
                placeholder="Select location"
                register={register("locOverNight", { required: true })}
                errors={errors.locOverNight}
                watch={watch("locOverNight")}
                trigger={trigger}
                isSearchable={true}
                isLoading={isLoading}
              />
              <RadioGroup
                name="overNightHome"
                mainLabel="Is your vehicle kept at your home address overnight?"
                register={register}
                trigger={trigger}
                errors={errors.overNightHome}
                onYes={resetGarageAddress}
                onNo={resetGarageAddress}
              />

              {isGarageAddressRequired && (
                <div>
                  <AddressLookup
                    register={register}
                    watch={watch}
                    control={control}
                    setValue={setValue}
                    trigger={trigger}
                    errors={errors}
                    STATE={STATE}
                    scope="garage"
                    labelHouse="House name or number"
                    labelPostCode="Overnight postcode"
                    autoLookup
                  />
                  {addressError && !isGarageAddressSelected && (
                    <span className="tw-mt-[5px] tw-block tw-text-sm tw-text-dis-error">
                      {addressError}
                    </span>
                  )}
                </div>
              )}

              <RadioGroup
                name="AdditionalClaims5Years"
                mainLabel="Have you had any accidents, losses, thefts, fire, flood or
                claims in any vehicle regardless of fault or whether you claimed
                or not in the last 5 years?"
                register={register}
                trigger={trigger}
                errors={errors.AdditionalClaims5Years}
              />

              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <div className="tw-flex tw-items-start tw-gap-3">
                  <input
                    type="checkbox"
                    className="tw-landing-native-checkbox tw-m-0 tw-mt-1 tw-h-5 tw-w-5 tw-min-w-5 tw-shrink-0 tw-cursor-pointer tw-accent-dis-deep-blue tw-p-0 tw-shadow-none [&:checked::after]:tw-content-none"
                    id="termsAccepted"
                    {...register("termsAccepted", {
                      required:
                        "You cannot obtain a quote without confirming acceptance our Terms of Business and Privacy Policy",
                    })}
                  />
                  <label
                    htmlFor="termsAccepted"
                    className="tw-m-0 tw-cursor-pointer tw-text-base tw-leading-6 tw-text-dis-text"
                  >
                    I confirm that I have read and understood the{" "}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        downloadTOBA({ navigate });
                      }}
                      className="tw-cursor-pointer tw-bg-transparent tw-p-0 tw-text-dis-deep-blue tw-underline hover:tw-bg-transparent"
                    >
                      Terms of Business
                    </a>{" "}
                    and{" "}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        downloadPrivacyPolicy({ navigate });
                      }}
                      className="tw-cursor-pointer tw-bg-transparent tw-p-0 tw-text-dis-deep-blue tw-underline hover:tw-bg-transparent"
                    >
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>

                {errors.termsAccepted && (
                  <span className="tw-ml-8 tw-block tw-text-sm tw-text-dis-error">
                    {errors.termsAccepted.message}
                  </span>
                )}
              </div>
            </>
          </FieldStack>
        </QuoteFormWrapper>
        <FormNavigationButtons
          prevStep={QuoteStep.TQ5}
          isValid={
            isValid &&
            !isGarageManualAddressOpen &&
            (!isGarageAddressRequired || isGarageAddressSelected)
          }
        />
      </form>
    </>
  );
};

export default Tq6;
